import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;
 

const KudosSection = ({ isAdmin = false }) => {
  const [staff, setStaff] = useState([]);
  const [kudos, setKudos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ to_user_id: '', note: '', gift: 'light_meals' });
  const [saving, setSaving] = useState(false);
  const { user } = useAuth();
  const [hasSubmittedThisQuarter, setHasSubmittedThisQuarter] = useState(false);

  function _getCurrentYearQuarter() {
    const now = new Date();
    const year = now.getUTCFullYear();
    const month = now.getUTCMonth() + 1;
    const quarter = `Q${Math.floor((month - 1) / 3) + 1}`;
    return { year, quarter };
  }

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [staffRes, kudosRes] = await Promise.all([
          axios.get(`${API}/api/staff-list`, { withCredentials: true }),
          axios.get(`${API}/api/kudos`, { withCredentials: true }),
        ]);
        const staffList = Array.isArray(staffRes.data) ? staffRes.data : [];
        const kudosList = Array.isArray(kudosRes.data) ? kudosRes.data : [];
        setStaff(staffList);
        setKudos(kudosList);

        // Determine if current user already submitted this quarter
        if (user && user.id) {
          const { year, quarter } = _getCurrentYearQuarter();
          const submitted = kudosList.some((k) => {
            if (k.user_id !== user.id) return false;
            if (k.year !== undefined) return Number(k.year) === Number(year) && k.quarter === quarter;
            // fallback: check created_at
            if (k.created_at) {
              const d = new Date(k.created_at);
              const y = d.getUTCFullYear();
              const m = d.getUTCMonth() + 1;
              const q = `Q${Math.floor((m - 1) / 3) + 1}`;
              return y === year && q === quarter;
            }
            return false;
          });
          setHasSubmittedThisQuarter(Boolean(submitted));
        }
      } catch (err) {
        console.error(err);
        toast.error('Unable to load kudos or staff');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  const deleteKudos = async (id) => {
    if (!window.confirm('Delete this kudos?')) return;

    try {
      await axios.delete(`${API}/api/kudos/${id}`, { withCredentials: true });
      toast.success('Kudos deleted');
      const { data } = await axios.get(`${API}/api/kudos`, { withCredentials: true });
      setKudos(Array.isArray(data) ? data : []);
      window.dispatchEvent(new CustomEvent('kudos-updated'));
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || 'Failed to delete kudos';
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete kudos');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.to_user_id) return toast.error('Select a recipient');
    if (hasSubmittedThisQuarter) return toast.error('You have already submitted a kudos for the current quarter');
    setSaving(true);
    try {
      await axios.post(`${API}/api/kudos`, form, { withCredentials: true });
      toast.success('Kudos submitted');
      const { data } = await axios.get(`${API}/api/kudos`, { withCredentials: true });
      const newList = Array.isArray(data) ? data : [];
      setKudos(newList);
      setForm({ to_user_id: '', note: '', gift: 'light_meals' });
      window.dispatchEvent(new CustomEvent('kudos-updated'));
      // update the submitted flag after successful submission
      if (user && user.id) {
        const { year, quarter } = _getCurrentYearQuarter();
        const submitted = newList.some((k) => k.user_id === user.id && (k.year === undefined ? true : Number(k.year) === Number(year) && k.quarter === quarter));
        setHasSubmittedThisQuarter(Boolean(submitted));
      }
    } catch (err) {
      console.error(err);
      const msg = err.response?.data?.detail || 'Failed to submit kudos';
      toast.error(typeof msg === 'string' ? msg : 'Failed to submit kudos');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="bg-white border-slate-200">
      <CardHeader>
        <CardTitle className="text-xl font-medium">Kudos / Recognition</CardTitle>
        <CardDescription>Send recognition to colleagues and share kudos news.</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-md">
          <p className="text-sm text-blue-800 font-medium">💡 One teacher, one submission per quarter</p>
          <p className="text-xs text-blue-700 mt-1">Think wisely and be specific on why you recognize them.</p>
          {hasSubmittedThisQuarter && (
            <p className="mt-2 text-sm text-slate-700">You have already submitted a kudos for this quarter. You cannot submit another until next quarter.</p>
          )}
        </div>
        <div className="grid gap-4 md:grid-cols-2 mb-4">
            <div>
            <Label>Recipient</Label>
            <select disabled={hasSubmittedThisQuarter} value={form.to_user_id} onChange={(e) => setForm((p) => ({ ...p, to_user_id: e.target.value }))} className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2">
              <option value="">Select recipient</option>
              {staff.map((s) => (
                <option key={s.id} value={s.id}>{s.name} {s.role ? `(${s.role})` : ''}</option>
              ))}
            </select>
          </div>
            <div>
              <Label>Gift</Label>
            <select disabled={hasSubmittedThisQuarter} value={form.gift} onChange={(e) => setForm((p) => ({ ...p, gift: e.target.value }))} className="mt-2 block w-full rounded-md border border-slate-300 px-3 py-2">
              <option value="money">Money</option>
              <option value="light_meals">Light meals</option>
              <option value="drink">Drink</option>
            </select>
          </div>
          <div>
            <Label>Note</Label>
            <Textarea value={form.note} onChange={(e) => setForm((p) => ({ ...p, note: e.target.value }))} rows={3} />
          </div>
          <div className="md:col-span-2 flex justify-end">
            <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSubmit} disabled={saving || hasSubmittedThisQuarter}>{saving ? 'Sending…' : 'Send Kudos'}</Button>
          </div>
        </div>

        <div className="space-y-3">
          {loading ? (
            <div className="text-sm text-slate-500">Loading kudos…</div>
          ) : kudos.length === 0 ? (
            <div className="text-sm text-slate-500">No kudos yet.</div>
          ) : (
            kudos.map((k) => (
              <div key={k.id} className="p-3 border border-slate-200 rounded-lg bg-white">
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-600">From <strong>{k.user_name}</strong> to <strong>{k.to_user_name || k.to_user_id}</strong></div>
                    <div className="mt-2 text-sm text-slate-700">{k.note || '—'}</div>
                    <div className="mt-2 text-xs text-slate-500">{new Date(k.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className="bg-slate-100 text-slate-800">{k.gift} • {k.quarter}</Badge>
                    {isAdmin && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => deleteKudos(k.id)}
                        className="text-red-600"
                        data-testid={`delete-kudos-${k.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default KudosSection;
